import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { AlertCircle, Copy, Check, Loader, Zap, Shield, Clock } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { createCryptoPayment, getCryptoPaymentStatus } from '../../services/paymentService';

// ── Supported currencies ──────────────────────────────────────
const CRYPTO_OPTIONS = [
  { id: 'eth',  name: 'Ethereum',      network: 'ERC-20',         emoji: 'Ξ',  color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/40' },
  { id: 'btc',  name: 'Bitcoin',       network: 'Bitcoin Network', emoji: '₿',  color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/40' },
  { id: 'usdterc20', name: 'USDT',    network: 'ERC-20',          emoji: '₮',  color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/40' },
  { id: 'bnbbsc', name: 'BNB',        network: 'BSC (BEP-20)',    emoji: '⬡',  color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/40' },
  { id: 'matic', name: 'Polygon',     network: 'Polygon',         emoji: '⬡',  color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/40' },
];

// NOWPayments status → UI state
const STATUS_MAP = {
  waiting:     { label_he: 'ממתין לתשלום...', label_en: 'Waiting for payment...', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: Clock },
  confirming:  { label_he: 'מאשר עסקה...', label_en: 'Confirming transaction...', color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30',   icon: Loader },
  confirmed:   { label_he: 'אושר! מוסיף קרדיטים...', label_en: 'Confirmed! Adding credits...', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: Check },
  finished:    { label_he: 'תשלום הושלם! 🎉', label_en: 'Payment complete! 🎉', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: Check },
  failed:      { label_he: 'התשלום נכשל', label_en: 'Payment failed', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: AlertCircle },
  expired:     { label_he: 'פג תוקף', label_en: 'Expired', color: 'text-slate-400', bg: 'bg-slate-800', border: 'border-slate-700', icon: AlertCircle },
};

function QRCode({ value, size = 160 }) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=0f172a&color=f1f5f9&margin=8`;
  return <img src={url} alt="QR Code" width={size} height={size} className="rounded-xl mx-auto border border-slate-700" />;
}

// ── Main Component ────────────────────────────────────────────
export default function CryptoPayment({ plan, onSuccess, onError, onCancel }) {
  const { language } = useLanguage();
  const isHe = language === 'he';

  // ─ Step 1: select currency / Step 2: pay
  const [step, setStep] = useState('select');
  const [selectedCrypto, setSelectedCrypto] = useState('eth');
  const [copied, setCopied] = useState(false);

  // ─ Payment data from NOWPayments
  const [payment, setPayment] = useState(null);     // { payment_id, pay_address, pay_amount, pay_currency, ... }
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // ─ Auto-detection status
  const [payStatus, setPayStatus] = useState('waiting');
  const pollRef = useRef(null);

  const crypto = CRYPTO_OPTIONS.find(c => c.id === selectedCrypto);
  const usdAmount = plan?.price || 0;
  const creditsToAdd = plan?.totalCredits ?? plan?.credits ?? 0;

  // ── Poll payment status every 10s ────────────────────────────
  const startPolling = useCallback((paymentId) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const data = await getCryptoPaymentStatus(paymentId);
        const st = data.status;
        setPayStatus(st);

        if (st === 'finished' || st === 'confirmed') {
          clearInterval(pollRef.current);
          setTimeout(() => onSuccess({ provider: 'crypto_nowpayments', payment_id: paymentId }), 1500);
        } else if (st === 'failed' || st === 'expired') {
          clearInterval(pollRef.current);
        }
      } catch {}
    }, 10000);
  }, [onSuccess]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Create payment via paymentService (Firebase auth) ──────────────
  const createPayment = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const data = await createCryptoPayment({
        amount: usdAmount,
        credits: creditsToAdd,
        pay_currency: selectedCrypto,
      });
      setPayment(data);
      setPayStatus(data.status || 'waiting');
      setStep('pay');
      startPolling(data.payment_id);
    } catch (err) {
      setCreateError(err.message);
      onError(err);
    } finally {
      setIsCreating(false);
    }
  };

  const copyAddress = async () => {
    if (!payment?.pay_address) return;
    try {
      await navigator.clipboard.writeText(payment.pay_address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const statusInfo = STATUS_MAP[payStatus] || STATUS_MAP.waiting;
  const StatusIcon = statusInfo.icon;
  const isPaid = payStatus === 'finished' || payStatus === 'confirmed';
  const isFailed = payStatus === 'failed' || payStatus === 'expired';

  // ════════════════════════════════════════════════════════════
  // STEP 1 — Select currency
  // ════════════════════════════════════════════════════════════
  if (step === 'select') {
    return (
      <div className="space-y-5">
        {/* Plan summary */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
          <div>
            <p className="font-semibold text-white">{plan?.name}</p>
            <p className="text-sm text-slate-400">{creditsToAdd} {isHe ? 'קרדיטים' : 'credits'}</p>
          </div>
          <span className="text-2xl font-bold text-violet-400">${usdAmount}</span>
        </div>

        {/* Currency selector */}
        <p className="text-sm font-medium text-slate-300">{isHe ? 'בחר מטבע' : 'Select Currency'}</p>
        <div className="grid gap-2">
          {CRYPTO_OPTIONS.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedCrypto(c.id)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                selectedCrypto === c.id ? `${c.border} ${c.bg}` : 'border-slate-700 bg-slate-900 hover:border-slate-600'
              }`}
            >
              <span className={`text-xl font-black w-7 text-center ${c.color}`}>{c.emoji}</span>
              <div className="flex-1">
                <p className="font-semibold text-white text-sm">{c.name}</p>
                <p className={`text-xs ${c.color}`}>{c.network}</p>
              </div>
              {selectedCrypto === c.id && <Check className={`w-4 h-4 ${c.color}`} />}
            </button>
          ))}
        </div>

        {createError && (
          <div className="flex gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{createError}</span>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1 border-slate-700 text-slate-300">
            {isHe ? 'ביטול' : 'Cancel'}
          </Button>
          <Button
            onClick={createPayment}
            disabled={isCreating}
            className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 font-semibold"
          >
            {isCreating ? (
              <><Loader className="w-4 h-4 mr-2 animate-spin" />{isHe ? 'יוצר...': 'Creating...'}</>
            ) : (
              <><Zap className="w-4 h-4 mr-2" />{isHe ? 'המשך' : 'Continue'}</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // STEP 2 — Pay (auto-detecting)
  // ════════════════════════════════════════════════════════════
  return (
    <div className="space-y-4">
      {/* Amount to send */}
      <div className={`p-4 rounded-xl border ${crypto.border} ${crypto.bg} text-center`}>
        <p className="text-xs text-slate-400 mb-1">{isHe ? 'שלח בדיוק:' : 'Send exactly:'}</p>
        <p className={`text-3xl font-black ${crypto.color}`}>
          {payment?.pay_amount} {payment?.pay_currency?.toUpperCase()}
        </p>
        <p className="text-xs text-slate-500 mt-1">≈ ${usdAmount} USD</p>
        <p className="text-xs mt-1 text-slate-400">{crypto.network}</p>
      </div>

      {/* Auto-detect status badge */}
      <div className={`flex items-center gap-3 p-3 rounded-xl border ${statusInfo.border} ${statusInfo.bg}`}>
        {isPaid ? (
          <Check className={`w-5 h-5 flex-shrink-0 ${statusInfo.color}`} />
        ) : isFailed ? (
          <AlertCircle className={`w-5 h-5 flex-shrink-0 ${statusInfo.color}`} />
        ) : (
          <Loader className={`w-5 h-5 flex-shrink-0 ${statusInfo.color} animate-spin`} />
        )}
        <div>
          <p className={`text-sm font-semibold ${statusInfo.color}`}>
            {isHe ? statusInfo.label_he : statusInfo.label_en}
          </p>
          {!isPaid && !isFailed && (
            <p className="text-xs text-slate-500">
              {isHe ? 'בודק אוטומטית כל 10 שניות' : 'Auto-checking every 10 seconds'}
            </p>
          )}
        </div>
      </div>

      {/* QR Code */}
      {payment?.pay_address && !isPaid && (
        <div className="text-center">
          <p className="text-xs text-slate-400 mb-2">{isHe ? 'סרוק QR לתשלום:' : 'Scan QR to pay:'}</p>
          <QRCode value={payment.pay_address} size={148} />
        </div>
      )}

      {/* Wallet address */}
      {payment?.pay_address && !isPaid && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-300">{isHe ? 'כתובת ארנק:' : 'Wallet Address:'}</p>
          <div className="p-3 bg-slate-800 rounded-lg font-mono text-xs text-white break-all border border-slate-700">
            {payment.pay_address}
          </div>
          <Button variant="outline" size="sm" onClick={copyAddress} className="w-full border-slate-700 text-slate-300">
            {copied ? (
              <><Check className="w-4 h-4 mr-2 text-green-400" />{isHe ? 'הועתק!' : 'Copied!'}</>
            ) : (
              <><Copy className="w-4 h-4 mr-2" />{isHe ? 'העתק כתובת' : 'Copy Address'}</>
            )}
          </Button>
        </div>
      )}

      {/* Success state */}
      {isPaid && (
        <div className="text-center py-4 space-y-3">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full">
            <Check className="w-8 h-8 text-green-400" />
          </div>
          <p className="text-white font-bold text-lg">{isHe ? 'תשלום התקבל! 🎉' : 'Payment Received! 🎉'}</p>
          <p className="text-slate-400 text-sm">
            {isHe ? `${creditsToAdd} קרדיטים נוספו לחשבונך` : `${creditsToAdd} credits added to your account`}
          </p>
        </div>
      )}

      {/* Warning */}
      {!isPaid && !isFailed && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 space-y-1">
          <p className="font-semibold">{isHe ? '⚠️ חשוב:' : '⚠️ Important:'}</p>
          <p>• {isHe ? 'שלח את הסכום המדויק הרשום' : 'Send the exact amount shown'}</p>
          <p>• {isHe ? 'ודא שאתה שולח ל-Network הנכון' : 'Make sure you send on the correct network'}</p>
          <p>• {isHe ? 'אנחנו מזהים את העסקה אוטומטית — אין צורך לעשות כלום נוסף' : 'We detect your transaction automatically — no action needed'}</p>
        </div>
      )}

      {/* Security + payment ID */}
      <div className="flex items-center justify-between text-xs text-slate-600">
        <div className="flex items-center gap-1">
          <Shield className="w-3 h-3" />
          <span>Powered by NOWPayments</span>
        </div>
        {payment?.payment_id && (
          <span>ID: {payment.payment_id}</span>
        )}
      </div>

      {!isPaid && (
        <Button variant="outline" onClick={() => { setStep('select'); if (pollRef.current) clearInterval(pollRef.current); }} className="w-full border-slate-700 text-slate-300 text-sm">
          {isHe ? '← חזרה לבחירת מטבע' : '← Back to currency selection'}
        </Button>
      )}
    </div>
  );
}
